import React, { useEffect, useMemo } from "react";

import { useLazyQuery } from "@apollo/client";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  WatchLater as WatchLaterIcon,
} from "@mui/icons-material";
import { Avatar, Box, Button, IconButton, Typography } from "@mui/material";
import { makeStyles, withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { colors } from "@/theme/overrides/colors";
import { format } from "@/utils/datetime";
import { humanize } from "@/utils/string";

import StatusDotIndicator from "./StatusDotIndicator";
import { isAppointmentFinished } from "../utils/isAppointmentFinished";
import useAppointmentCurrent from "../utils/useAppointmentCurrent";

const drawerMarginCollapsed = -450;
const drawerMargin = 0;

const useStyles = makeStyles()((theme) => ({
  drawerContainer: {
    position: "fixed",
    top: 0,
    right: 0,
    marginTop: "60px",
    width: "450px",
    height: "100%",
    backgroundColor: colors.white,
    zIndex: theme.zIndex.drawer,
  },

  drawerOpen: {
    marginRight: drawerMargin,
    transition: theme.transitions.create("margin-right", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  drawerClose: {
    marginRight: drawerMarginCollapsed,
    transition: theme.transitions.create("margin-right", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  drawerShadow: {
    // taken from Card component box-shadow style
    boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.2)",
  },

  form: {
    padding: "20px",
  },

  titleText: {
    fontSize: 24,
    fontWeight: 700,
    color: colors.dark,
    marginBottom: "20px",
  },

  closeButton: {
    color: "#26C9A7",
  },

  appointmentTypeIconContainer: {
    background: colors.white,
    padding: theme.spacing(2),
    borderRadius: 50,
    height: 40,
    width: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #C8CED8",
    marginRight: "10px",
  },

  appointmentIcon: {
    color: colors.gray,
  },

  appointmentText: {
    color: colors.gray,
  },

  appointmentTypeContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "15px",
    border: "1px solid #C8CED8",
    width: "160px",
    cursor: "pointer",
    marginRight: "20px",
    marginBottom: "20px",
  },

  onlineAppointment: {
    backgroundColor: colors.secondary,
    border: colors.secondary,
  },

  onlineAppointmentIcon: {
    color: colors.secondary,
  },

  inPersonAppointment: {
    backgroundColor: "#26C9A7",
    border: "#26C9A7",
  },

  inPersonAppointmentIcon: {
    color: "#26C9A7",
  },

  selectedAppointmentText: {
    color: colors.white,
  },

  appointmentTitle: {
    fontSize: "16px",
    fontWeight: 700,
    color: colors.dark,
    marginBottom: "12px",
  },

  fieldContainer: {
    marginBottom: "20px",
  },

  fieldLabel: {
    color: colors.gray,
    fontSize: "14px",
    marginBottom: "12px",
  },

  fields: {
    width: "100%",
  },

  avatar: {
    width: "56px",
    height: "56px",
  },

  badge: {
    height: "21px",
    backgroundColor: "rgba(125, 135, 151, 0.15)",
    fontSize: "10px",
    color: colors.gray,
    borderColor: colors.gray,
    "&:hover": {
      backgroundColor: "rgba(125, 135, 151, 0.15)",
      color: colors.gray,
      borderColor: colors.gray,
    },
  },

  clientInfo: {
    marginBottom: "30px",
  },

  fullname: {
    marginTop: 4,
    fontSize: "20px",
    fontWeight: 700,
  },

  contact: {
    fontSize: "12px",
    fontWeight: 600,
    color: colors.gray,
  },

  scheduleInfo: {
    marginBottom: "30px",
  },

  time: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#0B1B26",
  },

  frequency: {
    marginTop: 4,
    fontSize: "14px",
    fontWeight: 700,
    color: "#0B1B26",
  },

  details: {
    marginTop: 4,
    fontSize: "12px",
    fontWeight: 600,
    color: colors.gray,
  },

  schedType: {
    fontSize: "14px",
    fontWeight: 600,
    color: colors.dark,
  },

  schedTypeIcon: {
    fontSize: "10px",
    marginRight: "5px",
  },

  upcommingIcon: {
    color: "#2D87BB",
  },

  currentIcon: {
    color: "#FFD98A",
  },

  cancelledIcon: {
    color: "#FF6262",
  },

  finishedIcon: {
    color: "#B8BEC9",
  },

  statusView: {
    display: "flex",
    alignItems: "center",
    marginTop: 4,
  },
}));

const StyledCancelButton = withStyles(Button, {
  root: {
    color: "#FF6262",
    border: "2px solid #FF6262",
    marginRight: 10,
    "&:hover": {
      color: "#FF6262",
      border: "2px solid #FF6262",
    },
  },
});

interface Props {
  id: string;
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  onCancel: () => void;
}

const viewAppointmentDetailsQuery = graphql(`
  query ViewAppointmentDetails($id: ID!) {
    appointment(id: $id) {
      id
      client {
        id
        email
        profile {
          id
          fullName
          mobileNumber
          avatarUrl(variant: THUMB)
        }
      }
      details
      title
      startTime
      endTime
      status
      appointmentType
      startDate
      endDate
      meetingDetails {
        roomUrl
      }
    }
  }
`);

const ViewAppointmentDetails: React.FC<Props> = ({
  id,
  open,
  onClose,
  onEdit,
  onCancel,
}) => {
  const { classes, cx } = useStyles();

  const [fetch, { data }] = useLazyQuery(viewAppointmentDetailsQuery, {
    variables: { id },
  });

  useEffect(() => {
    if (open) fetch();
  }, [open]);

  const appointment = data?.appointment;

  const client = appointment?.client;

  const startTime = format(
    { timeofday: appointment?.startTime ?? new Date() },
    "hh:mm a"
  );
  const endTime = format(
    { timeofday: appointment?.endTime ?? new Date() },
    "hh:mm a"
  );
  const startDate = format(
    { date: appointment?.startDate ?? new Date() },
    "iiii, dd MM, yyyy"
  );

  const isCurrent = useAppointmentCurrent(appointment ?? undefined);

  const appointmentStat = useMemo(() => {
    return isCurrent ? "current" : appointment?.status;
  }, [isCurrent]);

  const appointmentType =
    appointment?.appointmentType == "inperson" ? "In-person" : "Online";
  const appointmentStatus = isAppointmentFinished(appointment)
    ? "finished"
    : appointmentStat;
  const isNotFinished = appointmentStatus !== "finished";
  const isNotCancelled = appointmentStatus !== "cancelled";

  return (
    <div
      className={cx(classes.drawerContainer, [
        !open
          ? classes.drawerClose
          : [classes.drawerOpen, classes.drawerShadow],
      ])}
    >
      <div className={classes.form}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Typography className={classes.titleText}>
            Appointment Details
          </Typography>
          <IconButton
            className={classes.closeButton}
            size="small"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <div className={classes.clientInfo}>
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="flex-start"
          >
            <Box display="flex" alignItems="center" style={{ height: "100%" }}>
              <Avatar
                src={client?.profile?.avatarUrl ?? undefined}
                className={classes.avatar}
                alt={"User"}
              />
            </Box>
            <Box
              marginLeft="16px"
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              textAlign="start"
            >
              <Button
                className={classes.badge}
                size="small"
                variant="outlined"
                startIcon={<PersonIcon style={{ fontSize: "10px" }} />}
              >
                {`${appointmentType} Appointment`}
              </Button>
              <Typography variant="body1" className={classes.fullname}>
                {client?.profile?.fullName}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.contact}
              >
                {`${client?.email} | ${client?.profile?.mobileNumber ?? "N/A"}`}
              </Typography>
              <div className={classes.statusView}>
                <StatusDotIndicator status={appointmentStatus ?? ""} />
                <Typography className={classes.schedType}>
                  {humanize(appointmentStatus ?? "")}
                </Typography>
              </div>
            </Box>
          </Box>
        </div>

        <Box
          className={classes.scheduleInfo}
          display="flex"
          alignItems="flex-start"
          justifyContent="flex-start"
        >
          <Box display="flex" paddingRight={2}>
            <Typography>
              <WatchLaterIcon htmlColor={colors.gray} />
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.time}>
              {`${startDate} | ${startTime} - ${endTime}`}
            </Typography>
            <Typography className={classes.frequency}>
              {appointment?.title ?? "N/A"}
            </Typography>
            <Typography className={classes.details}>
              {appointment?.details ?? "N/A"}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" justifyContent="center" alignItems="center">
          {isNotFinished && isNotCancelled && (
            <>
              <StyledCancelButton variant="outlined" onClick={onCancel}>
                Cancel Appointment
              </StyledCancelButton>

              <Button color="primary" onClick={onEdit}>
                Edit Appointment
              </Button>
            </>
          )}
        </Box>
      </div>
    </div>
  );
};

export default ViewAppointmentDetails;
